import React, { useCallback, useEffect, useRef, useState } from "react";
import "./OpenOrder.scss";
import CardHeading from "../../../components/common/CardHeading/CardHeading.jsx";
import { Nav, Tab } from "react-bootstrap";
import OrderTable from "./OrderTable/OrderTable.jsx";
import { useDispatch, useSelector } from "react-redux";
import { ATS } from "../../../features/ats/atsSlice";
import { refreshActivePairBalance, refreshActivePairBalanceAfterSettlement, refreshActivePairBalanceOnSettlementConfirmed, refreshActivePairBalanceOnOrderCompleted, isSettlementFillOrderUpdate, isUserTradeSocketEvent, isOrderFullyCompleted } from "../../../services/wallet/tradeBalanceRefresh.service";
import { socket } from "../../../App";
import { SOCKET, socketPairTopicPrefix } from "../../../constants/constants";
import { registerExchangePairPostJoin } from "../../../services/exchangePairRoomSocket";
import TradeTable from "./TradeTable/TradeTable.jsx";
import { parseJSON } from "../../../helpers/commonHelper";
import OrderHistoryTable from "./OrderHistory/OrderHistory.jsx";
import ProfitAndLoss from "./ProfitAndLoss/ProfitAndLoss.jsx";
import { buildOrderHistoryRowFromSocket, isEligibleOrderHistoryRow, normalizeOrderHistoryRow } from "./OrderHistory/orderHistoryMerge";
import { setCachedOpenOrders } from "../../../services/wallet/openOrdersCache.service";
const OpenOrder = ({
  sectionRef
}) => {
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [userOrderList, setUserOrderList] = useState([]);
  const [paginationBody, setPaginationBody] = useState({
    page: 1,
    limit: 10,
    pairName: '',
    status: 1
  });
  const [loadMore, setIsLoadMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /** Spinner / "Loading" label on Orders tab only after Load More is clicked. */
  const [ordersLoadMoreBusy, setOrdersLoadMoreBusy] = useState(false);
  const [userTrade, setUserTrade] = useState(null);
  const [userActionOrder, setUserActionOrder] = useState(null);
  const [orderHistoryRefresh, setOrderHistoryRefresh] = useState(false);
  const orderListRef = useRef(userOrderList);
  const activeRequestIdRef = useRef(0);
  orderListRef.current = userOrderList;
  const selectedPair = useSelector(state => state.ats.selectedPair);
  const selectedTradeWallet = useSelector(state => state.ats.selectedTradeWallet);
  const lastTrade = useSelector(state => state.ats.lastTrade);
  const selectedPairRef = useRef(selectedPair);
  const selectedTradeWalletRef = useRef(selectedTradeWallet);
  selectedPairRef.current = selectedPair;
  selectedTradeWalletRef.current = selectedTradeWallet;
  const lastTradeRefreshRef = useRef(null);
  const setOpenOrderRootRef = useCallback(node => {
    rootRef.current = node;
    if (typeof sectionRef === "function") {
      sectionRef(node);
    } else if (sectionRef) {
      sectionRef.current = node;
    }
  }, [sectionRef]);
  useEffect(() => {
    const syncFullscreen = () => {
      const el = rootRef.current;
      const active = document.fullscreenElement === el || document.webkitFullscreenElement === el;
      setIsFullscreen(Boolean(active));
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);
    syncFullscreen();
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
    };
  }, []);
  useEffect(() => {
    if (selectedPair?.pair_key) {
      setCachedOpenOrders(selectedPair.pair_key, userOrderList);
    }
  }, [selectedPair?.pair_key, userOrderList]);
  const fetchUserOrderList = async (body, mode = "replace") => {
    const appendPage = mode === "append";
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    setIsLoading(true);
    if (appendPage) {
      setOrdersLoadMoreBusy(true);
    } else {
      setOrdersLoadMoreBusy(false);
    }
    try {
      const response = await ATS.fetchUserOrderList(body);
      if (requestId !== activeRequestIdRef.current) return;
      if (response?.status === 200 && response?.data?.orderlist) {
        if (response.data.orderlist.length < 10) setIsLoadMore(false);else setIsLoadMore(true);
        appendPage ? setUserOrderList([...orderListRef.current, ...response.data.orderlist]) : setUserOrderList([...response.data.orderlist]);
      }
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setIsLoading(false);
        setOrdersLoadMoreBusy(false);
      }
    }
  };
  const refreshOpenOrders = pairName => {
    const nextPagination = {
      page: 1,
      limit: 10,
      pairName,
      status: 1
    };
    setPaginationBody(nextPagination);
    setIsLoadMore(true);
    fetchUserOrderList(nextPagination, "replace");
  };
  const onPagination = () => {
    if (isLoading) return;
    const nextPagination = {
      ...paginationBody,
      page: paginationBody.page + 1
    };
    setPaginationBody(nextPagination);
    fetchUserOrderList(nextPagination, "append");
  };
  const resolveOrderStatusAfterFill = (quantity, filledAmount) => {
    const qty = Number(quantity);
    const filled = Number(filledAmount);
    if (!Number.isFinite(qty) || qty <= 0) return 1;
    if (!Number.isFinite(filled) || filled <= 0) return 1;
    return filled >= qty ? 3 : 4;
  };
  const refreshBalancesForSelectedWallet = async (mode = 'normal') => {
    const ctx = {
      selectedPair: selectedPairRef.current,
      selectedTradeWallet: selectedTradeWalletRef.current
    };
    if (mode === 'tradeMatch') {
      refreshActivePairBalanceAfterSettlement(dispatch, ctx);
      return;
    }
    if (mode === 'settlementConfirmed' || mode === 'orderCompleted') {
      if (mode === 'orderCompleted') {
        refreshActivePairBalanceOnOrderCompleted(dispatch, ctx);
      } else {
        refreshActivePairBalanceOnSettlementConfirmed(dispatch, ctx);
      }
      return;
    }
    await refreshActivePairBalance(dispatch, ctx);
  };
  const handleSocketEvent = async (type, eventStatus, order) => {
    const parsedOrder = parseJSON(order);
    if (type === 'add') {
      if (parsedOrder.status == null || parsedOrder.status === undefined) {
        parsedOrder.status = 1;
      }
      const index = orderListRef.current.findIndex(o => Number(o.id) === Number(parsedOrder.id));
      parsedOrder.isFromSocket = true;
      if (index === -1) {
        orderListRef.current.unshift(parsedOrder);
      } else {
        orderListRef.current[index] = {
          ...orderListRef.current[index],
          ...parsedOrder,
          isFromSocket: true
        };
      }
      setUserOrderList(parseJSON(orderListRef.current));

      // Pending open order — refresh allocated balance. Fills are handled by USER_TRADE.
      if (!isSettlementFillOrderUpdate(parsedOrder) && !isOrderFullyCompleted(parsedOrder)) {
        await refreshBalancesForSelectedWallet('normal');
      }
      return;
    }
    const index = orderListRef.current.findIndex(o => Number(o.id) === Number(parsedOrder.id));
    if (index > -1) {
      if (eventStatus === 3) {
        const tradeFill = Number(parsedOrder.filled_amount) || 0;
        const prevFilled = Number(orderListRef.current[index].filled_amount) || 0;
        const cumulativeFilled = prevFilled + tradeFill;
        orderListRef.current[index].executed_price = parsedOrder.executed_price;
        orderListRef.current[index].filled_amount = cumulativeFilled;
        orderListRef.current[index].status = resolveOrderStatusAfterFill(orderListRef.current[index].quantity, cumulativeFilled);
      } else {
        if (parsedOrder.quantity != null) {
          const nextQuantity = Number(parsedOrder.quantity);
          if (Number.isFinite(nextQuantity) && nextQuantity > 0) {
            orderListRef.current[index].quantity = parsedOrder.quantity;
          }
        }
        if (parsedOrder.price != null) {
          orderListRef.current[index].price = parsedOrder.price;
        }
        orderListRef.current[index].status = eventStatus;
      }
      setUserOrderList(parseJSON(orderListRef.current));
      const openOrderRow = orderListRef.current[index];
      const historyRow = normalizeOrderHistoryRow(openOrderRow);
      if (isEligibleOrderHistoryRow(historyRow)) {
        setUserActionOrder(parseJSON(historyRow));
      }
      const removeFromOpenList = eventStatus !== 3 || Number(openOrderRow.status) === 3;
      if (removeFromOpenList) {
        orderListRef.current.splice(index, 1);
      }
      setUserOrderList(parseJSON(orderListRef.current));
      const fullyCompleted = eventStatus === 3 && Number(openOrderRow.status) === 3;
      await refreshBalancesForSelectedWallet(fullyCompleted ? 'orderCompleted' : eventStatus === 3 ? 'tradeMatch' : 'normal');
      return;
    }

    // Trade for an order not in the current list (pagination / missed socket) — reconcile from API.
    if (eventStatus === 3) {
      const pairKey = selectedPairRef.current?.pair_key;
      if (pairKey) {
        refreshOpenOrders(pairKey);
      }
      if (isUserTradeSocketEvent(parsedOrder)) {
        await refreshBalancesForSelectedWallet(isOrderFullyCompleted(parsedOrder) ? 'orderCompleted' : 'tradeMatch');
      }
      const historyRow = buildOrderHistoryRowFromSocket(parsedOrder, eventStatus);
      if (isEligibleOrderHistoryRow(historyRow)) {
        setUserActionOrder(historyRow);
      }
      return;
    }
    const historyRow = buildOrderHistoryRowFromSocket(parsedOrder, eventStatus);
    if (isEligibleOrderHistoryRow(historyRow)) {
      setUserActionOrder(historyRow);
    }
  };
  useEffect(() => {
    if (!selectedPair?.pair_key) return;
    const p = socketPairTopicPrefix(selectedPair.pair_key);
    const evOpen = `${p}${SOCKET.OPEN_ORDERS}`;
    const evCancel = `${p}${SOCKET.USER_CANCEL_ORDER}`;
    const evTrade = `${p}${SOCKET.USER_TRADE}`;
    const evSpeedbump = `${p}${SOCKET.SPEEDBUMP_USER_TRADE}`;
    const attachUserOrderSocket = () => {
      socket.off(evOpen);
      socket.off(evCancel);
      socket.off(evTrade);
      socket.off(evSpeedbump);
      socket.on(evOpen, order => {
        handleSocketEvent("add", 1, order);
      });
      socket.on(evCancel, order => {
        handleSocketEvent("cancel", 2, order);
      });
      socket.on(evTrade, order => {
        setUserTrade(order);
        handleSocketEvent("match", 3, order);
        const pairKey = selectedPairRef.current?.pair_key;
        if (pairKey) refreshOpenOrders(pairKey);
      });
      socket.on(evSpeedbump, order => {
        handleSocketEvent("speedbump", 6, order);
      });
      console.log("[OpenOrder] listening", {
        pair_key: selectedPair.pair_key,
        prefix: p,
        events: [evOpen, evCancel, evTrade, evSpeedbump]
      });
    };
    attachUserOrderSocket();
    const unregisterPostJoin = registerExchangePairPostJoin(() => {
      const key = selectedPairRef.current?.pair_key;
      if (key) refreshOpenOrders(key);
    });
    return () => {
      unregisterPostJoin();
      socket.off(evOpen);
      socket.off(evCancel);
      socket.off(evTrade);
      socket.off(evSpeedbump);
    };
  }, [selectedPair?.pair_key]);
  useEffect(() => {
    if (selectedPair?.pair_key) {
      const currentPairName = selectedPair.pair_key;
      const tableRoot = document.getElementsByClassName("orderTable")[0];
      const scrollEl = tableRoot?.firstChild?.firstChild;
      if (scrollEl && typeof scrollEl.scroll === "function") {
        scrollEl.scroll(0, 0);
      }
      refreshOpenOrders(currentPairName);
    }
  }, [selectedPair]);
  // Market trade — reconcile open orders when the public trade stream updates.
  useEffect(() => {
    if (!selectedPair?.pair_key || !lastTrade) return;
    if (lastTradeRefreshRef.current) clearTimeout(lastTradeRefreshRef.current);
    lastTradeRefreshRef.current = setTimeout(() => {
      refreshOpenOrders(selectedPair.pair_key);
    }, 200);
    return () => {
      if (lastTradeRefreshRef.current) clearTimeout(lastTradeRefreshRef.current);
    };
  }, [lastTrade, selectedPair?.pair_key]);
  useEffect(() => {
    const handleRefreshOrders = event => {
      const pairName = event?.detail?.pairName || selectedPair?.pair_key;
      if (!pairName || selectedPair?.pair_key && pairName !== selectedPair.pair_key) return;
      refreshOpenOrders(pairName);
    };
    window.addEventListener("open-orders:refresh", handleRefreshOrders);
    return () => {
      window.removeEventListener("open-orders:refresh", handleRefreshOrders);
    };
  }, [selectedPair]);
  return <div className={`openOrders${isFullscreen ? " is-fullscreen" : ""}`} ref={setOpenOrderRootRef}>
      <div className="openOrders__panel">
        <div className="openOrders_openHeader d-flex justify-content-between">
          <CardHeading title="Open Orders" />
        </div>
        <div className="openOrders_tabsRoot">
          <Tab.Container id="left-tabs-example" defaultActiveKey="orders">
            <Nav variant="pills" className="borderTabs">
              <Nav.Item>
                <Nav.Link eventKey="orders">Orders</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="trades">Trades</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="orderHistory">Order History</Nav.Link>
              </Nav.Item>
              {/* <Nav.Item>
                <Nav.Link eventKey="pnlTable">P/L</Nav.Link>
               </Nav.Item> */}
            </Nav>
            <Tab.Content className="mt-2">
              <Tab.Pane eventKey="orders">
                <OrderTable dataList={userOrderList} selectedPair={selectedPair} onLoadMore={onPagination} loadMore={loadMore} isListFetching={isLoading} loadMoreFetching={ordersLoadMoreBusy} />
              </Tab.Pane>
              <Tab.Pane eventKey="trades">
                <TradeTable trade={userTrade} />
              </Tab.Pane>
              <Tab.Pane eventKey="orderHistory">
                <OrderHistoryTable order={userActionOrder} isRfq={orderHistoryRefresh} />
              </Tab.Pane>
              <Tab.Pane eventKey="pnlTable">
                <ProfitAndLoss />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </div>
    </div>;
};
export default OpenOrder;